/**
 * i18next for the browser client.
 *
 * The same catalogue the phone uses, from `@nsibi/i18n` - not a second
 * one. web.nsibi.app renders the same chat through `@nsibi/ui-rn`, so
 * a separate catalogue would be two places for "Send" to disagree and
 * two places to keep in step with every future string.
 *
 * ── Detection differs from the phone in one way ────────────────────
 * There is no OS locale list to ask for, only `navigator.languages`,
 * which is the browser's own ordering and is the same shape. The
 * stored choice lives in localStorage rather than MMKV. Everything
 * else - the precedence, the fallback, the collapse of `fr-CM` onto
 * `fr` - is `resolveLocale` from the shared package, so the two clients
 * cannot drift on the rule that matters.
 *
 * Signed-in pages do NOT read the account's locale here. The browser is
 * a secondary device linked to a phone, and its own language setting is
 * the more immediate signal about who is sitting in front of it.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LOCALE, OFFERED_LOCALES, SYSTEM, directionOf, resolveLocale } from '@nsibi/i18n';
import de from '@nsibi/i18n/catalogue/de.json';
import en from '@nsibi/i18n/catalogue/en.json';
import es from '@nsibi/i18n/catalogue/es.json';
import fr from '@nsibi/i18n/catalogue/fr.json';
import pt from '@nsibi/i18n/catalogue/pt.json';
import sw from '@nsibi/i18n/catalogue/sw.json';

const CATALOGUES: Record<string, Record<string, string>> = { en, fr, pt, sw, es, de };

const STORAGE_KEY = 'nsibi.locale';

/** What the user chose here, or SYSTEM, or null. */
export function storedChoice(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode, or storage blocked. Not knowing is the same as
    // never having been told.
    return null;
  }
}

export function currentLocale(): string {
  return resolveLocale({
    stored: storedChoice(),
    device: typeof navigator === 'undefined' ? [] : [...(navigator.languages ?? [])],
  });
}

/** Apply a choice and repaint. `SYSTEM` hands control back to the browser. */
export async function setAppLocale(choice: string): Promise<void> {
  try {
    if (choice === SYSTEM) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    /* storage blocked - the change still applies for this session */
  }
  const code = currentLocale();
  await i18n.changeLanguage(code);
  applyDocumentLocale(code);
}

/**
 * Tell the DOCUMENT what language it is in.
 *
 * `lang` is what a screen reader picks a voice from and what a browser
 * offers to translate against; `dir` is what makes the page mirror.
 * Both are set here rather than in index.html because index.html is
 * static and this is not knowable until the browser is running.
 */
export function applyDocumentLocale(code: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = code;
  document.documentElement.dir = directionOf(code);
}

if (!i18n.isInitialized) {
  const lng = currentLocale();
  void i18n.use(initReactI18next).init({
    lng,
    fallbackLng: DEFAULT_LOCALE,
    interpolation: { escapeValue: false },
    resources: Object.fromEntries(
      OFFERED_LOCALES.map((l) => [l.code, { translation: CATALOGUES[l.code] ?? {} }]),
    ),
    react: { useSuspense: false },
  });
  applyDocumentLocale(lng);
}

export default i18n;
